import type { Column } from '@talos/kyoko';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { FundingRateGroupBy, type FundingRateRow } from './FundingRateBlotter.types';
import { FundingRateCell, OpenInterestCell } from './fundingRateBlotterCells';
import { nullsLastNumericComparator } from './fundingRateBlotterSort';

/**
 * List-view columns: Asset (icon + symbol), Exchange, Current Rate (sign-colored %, with a hover
 * tooltip), Open Interest. All sortable; Current Rate sorts descending by default. `groupBy` hides
 * and row-groups the Asset or Exchange column so the auto group column renders the grouping.
 */
export function useFundingRateBlotterColumns(
  groupBy: FundingRateGroupBy,
  periodLabel: string
): Column<unknown, FundingRateRow>[] {
  const theme = useTheme();

  return useMemo(
    () => [
      {
        type: 'currency',
        field: 'symbol',
        title: 'Asset',
        width: 160,
        rowGroup: groupBy === FundingRateGroupBy.Asset,
        hide: groupBy === FundingRateGroupBy.Asset,
      },
      {
        type: 'text',
        field: 'exchangeDisplayName',
        title: 'Exchange',
        flex: 1,
        rowGroup: groupBy === FundingRateGroupBy.Exchange,
        hide: groupBy === FundingRateGroupBy.Exchange,
      },
      {
        type: 'custom',
        field: 'fundingRatePct',
        title: 'Current Rate',
        // Default sort: highest rate first (matches the design).
        sort: '-',
        flex: 1,
        params: {
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
          cellRenderer: FundingRateCell,
          cellRendererParams: { periodLabel, tooltipPadding: theme.spacing12 },
          comparator: nullsLastNumericComparator,
        },
      },
      {
        type: 'custom',
        field: 'openInterestUsd',
        title: 'Open Interest',
        flex: 1,
        params: {
          headerClass: 'ag-right-aligned-header',
          cellClass: 'ag-right-aligned-cell',
          cellRenderer: OpenInterestCell,
          comparator: nullsLastNumericComparator,
        },
      },
    ],
    [groupBy, periodLabel, theme.spacing12]
  );
}
